import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  ClientCredentialDTO,
  ClientDTO,
  CreateClientCredentialRequest,
  CreateClientCredentialResponse,
  CreateClientRequest,
} from "../../../features/data-explorer/types/ClientCredential";
import { createServiceBaseQuery } from "../../client/serviceBaseQuery";

export const clientCredentialsService = createApi({
  reducerPath: "clientCredentialsService",
  baseQuery: createServiceBaseQuery("aiakosUri"),
  tagTypes: ["ClientCredential", "Client"],
  endpoints: (builder) => ({
    getClients: builder.query<ClientDTO[], void>({
      query: () => "api/clients",
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Client" as const, id })),
              { type: "Client", id: "LIST" },
            ]
          : [{ type: "Client", id: "LIST" }],
    }),

    getClientCredentials: builder.query<ClientCredentialDTO[], void>({
      query: () => "api/credential/clients",
      providesTags: [{ type: "ClientCredential", id: "LIST" }],
    }),

    getClientCredential: builder.query<ClientCredentialDTO, number>({
      query: (id) => `api/credential/clients/${id}`,
      providesTags: (_result, _error, id) => [{ type: "ClientCredential", id }],
    }),

    createClientCredential: builder.mutation<
      CreateClientCredentialResponse,
      CreateClientCredentialRequest
    >({
      query: (credential) => ({
        url: "api/credential/clients",
        method: "POST",
        body: credential,
      }),
      invalidatesTags: [{ type: "ClientCredential", id: "LIST" }],
    }),

    deleteClientCredential: builder.mutation<void, number>({
      query: (id) => ({
        url: `api/credential/clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ClientCredential", id: "LIST" }],
    }),

    createClient: builder.mutation<ClientDTO, CreateClientRequest>({
      query: (client) => ({
        url: "api/clients",
        method: "POST",
        body: client,
      }),
      invalidatesTags: [{ type: "Client", id: "LIST" }],
    }),

    updateClient: builder.mutation<ClientDTO, ClientDTO>({
      query: (client) => ({
        url: `api/clients/${client.id}`,
        method: "PUT",
        body: client,
      }),
      // Optimistic update preserves the cached object reference so AG Grid
      // doesn't reset its scroll position when the mutation settles.
      async onQueryStarted(updatedClient, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          clientCredentialsService.util.updateQueryData(
            "getClients",
            undefined,
            (draft) => {
              const client = draft.find((c) => c.id === updatedClient.id);
              if (client) {
                Object.assign(client, updatedClient);
              }
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;
          dispatch(
            clientCredentialsService.util.updateQueryData(
              "getClients",
              undefined,
              (draft) => {
                const client = draft.find((c) => c.id === data.id);
                if (client) {
                  Object.assign(client, data);
                }
              },
            ),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteClient: builder.mutation<void, number>({
      query: (id) => ({
        url: `api/clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Client", id: "LIST" }],
    }),
  }),
});

export const {
  useGetClientsQuery,
  useGetClientCredentialsQuery,
  useGetClientCredentialQuery,
  useCreateClientCredentialMutation,
  useDeleteClientCredentialMutation,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
} = clientCredentialsService;
